import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Button,
  TextInput,
  Group,
  Badge,
  ActionIcon,
  Modal,
  Stack,
  Alert,
  Loader,
  Pagination,
  Select,
  Box,
  Grid,
  Divider,
  Tabs
} from '@mantine/core';
import {
  IconSearch,
  IconEdit,
  IconTrash,
  IconEye,
  IconPlus,
  IconChartBar,
  IconTrendingUp,
  IconCreditCard,
  IconReceipt,
  IconMail,
  IconReport
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { API_URL } from '../../../utils/constants';

const AgentsListScreen = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAgents, setTotalAgents] = useState(0);
  const [sortBy, setSortBy] = useState('agent_number');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [agentTypes, setAgentTypes] = useState([]);
  const [agentCommissions, setAgentCommissions] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Separate useEffect for initial load only
  useEffect(() => {
    console.log('AgentsListScreen initial useEffect triggered');
    
    // Reset selected agent when page loads
    setSelectedAgent(null);
    
    fetchAgentTypes();
    fetchAgentCommissions();
    fetchAgents();
  }, []);

  // Separate useEffect for search/sort changes
  useEffect(() => {
    console.log('AgentsListScreen search/sort useEffect triggered');
    fetchAgents();
  }, [currentPage, sortBy, sortOrder, searchTerm]);

  const fetchAgentTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/agent-types`);
      if (response.ok) {
        const data = await response.json();
        setAgentTypes(data);
      }
    } catch (error) {
      console.error('Error fetching agent types:', error);
    }
  };

  const fetchAgentCommissions = async () => {
    try {
      const response = await fetch(`${API_URL}/agent-commissions`);
      if (response.ok) {
        const data = await response.json();
        setAgentCommissions(data);
      }
    } catch (error) {
      console.error('Error fetching agent commissions:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`${API_URL}/agents?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch agents');
      }

      const data = await response.json();
      setAgents(data.agents);
      setTotalPages(data.pagination.totalPages);
      setTotalAgents(data.pagination.totalAgents);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setError('Error fetching agents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setActiveTab('details');
  };

  const handleEdit = (agent) => {
    setEditingAgent({ ...agent });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API_URL}/agents/${editingAgent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingAgent),
      });

      if (!response.ok) {
        throw new Error('Failed to update agent');
      }

      const updatedAgent = await response.json();
      setAgents(prev => prev.map(agent => 
        agent.id === updatedAgent.agent.id ? updatedAgent.agent : agent
      ));
      setShowEditModal(false);
      setEditingAgent(null);
      notifications.show({
        title: 'Success',
        message: 'Agent updated successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Error updating agent:', error);
      setError('Error updating agent. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingAgent(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingAgent(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (agent) => {
    setAgentToDelete(agent);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleteLoading(true);
      const response = await fetch(`${API_URL}/agents/${agentToDelete.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete agent');
      }

      setShowDeleteModal(false);
      setAgentToDelete(null);
      fetchAgents();
      notifications.show({
        title: 'Success',
        message: 'Agent deleted successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Error deleting agent:', error);
      setError('Error deleting agent. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && agents.length === 0) {
    return (
      <Container fluid p={0} style={{ height: '100vh' }}>
        <Group justify="center" style={{ height: '100%' }}>
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  return (
    <Container fluid p={0} style={{ height: '100vh' }}>
      <Grid gutter={0} style={{ height: '100%' }}>
        {/* Side Menu */}
        <Grid.Col span={1.2} style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '0.5rem', borderRight: '1px solid var(--mantine-color-gray-2)' }}>
          <Stack gap="xs">
            <Title order={6} c="dark" mb="xs" ta="center">Agents</Title>
            
            {/* Primary Actions */}
            <Button 
              variant="subtle" 
              color="blue"
              fullWidth
              size="sm"
              onClick={() => window.location.href = '/dashboard/agents-find'}
              leftSection={<IconSearch size="0.8rem" />}
            >
              Find
            </Button>
            <Button 
              variant="subtle" 
              color="green"
              fullWidth
              size="sm"
              leftSection={<IconPlus size="0.8rem" />}
              onClick={() => window.location.href = '/dashboard/agents-add'}
            >
              Add
            </Button>
            <Button 
              variant="subtle" 
              color="orange"
              fullWidth
              size="sm"
              onClick={() => selectedAgent && handleEdit(selectedAgent)}
              disabled={!selectedAgent}
              leftSection={<IconEdit size="0.8rem" />}
            >
              Change
            </Button>
            <Button 
              variant="subtle" 
              color="red"
              fullWidth
              size="sm"
              onClick={() => selectedAgent && handleDelete(selectedAgent)}
              disabled={!selectedAgent}
              leftSection={<IconTrash size="0.8rem" />}
            >
              Delete
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Agent-specific buttons */}
            <Button 
              variant="subtle" 
              color="cyan"
              fullWidth
              size="sm"
              leftSection={<IconChartBar size="0.8rem" />}
            >
              Commission Report
            </Button>
            <Button 
              variant="subtle" 
              color="teal"
              fullWidth
              size="sm"
              leftSection={<IconTrendingUp size="0.8rem" />}
            >
              Performance Report
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Financial buttons */}
            <Button 
              variant="subtle" 
              color="indigo"
              fullWidth
              size="sm"
              leftSection={<IconCreditCard size="0.8rem" />}
            >
              Post Commission
            </Button>
            <Button 
              variant="subtle" 
              color="violet"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
            >
              Payment
            </Button>
            <Button 
              variant="subtle" 
              color="purple"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
            >
              Refund
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Communication and Reports buttons */}
            <Button 
              variant="subtle" 
              color="grape"
              fullWidth
              size="sm"
              leftSection={<IconMail size="0.8rem" />}
            >
              Emails
            </Button>
            <Button 
              variant="subtle" 
              color="dark"
              fullWidth
              size="sm"
              leftSection={<IconReport size="0.8rem" />}
            >
              Reports
            </Button>
          </Stack>
        </Grid.Col>

        {/* Main Content */}
        <Grid.Col span={10.8} style={{ padding: '1rem' }}>
          <Stack gap="lg">
            <Group justify="space-between">
              <Title order={1}>Agents List</Title>
            </Group>

            {error && (
              <Alert color="red" title="Error">
                {error}
              </Alert>
            )}

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group mb="md">
                <TextInput
                  placeholder="Search agents by name, number, or city..."
                  leftSection={<IconSearch size="1rem" />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ flex: 1 }}
                />
                <Button onClick={handleSearch}>Search</Button>
                <Button variant="outline" onClick={() => setSearchTerm('')} disabled={!searchTerm}>
                  Clear
                </Button>
              </Group>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th onClick={() => handleSort('agent_number')} style={{ cursor: 'pointer' }}>
                      Agent # {renderSortIcon('agent_number')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                      Name {renderSortIcon('name')}
                    </Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Commission</Table.Th>
                    <Table.Th onClick={() => handleSort('city')} style={{ cursor: 'pointer' }}>
                      City {renderSortIcon('city')}
                    </Table.Th>
                    <Table.Th>State</Table.Th>
                    <Table.Th onClick={() => handleSort('active')} style={{ cursor: 'pointer' }}>
                      Status {renderSortIcon('active')}
                    </Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {agents.map((agent) => (
                    <Table.Tr 
                      key={agent.id} 
                      onClick={() => handleAgentSelect(agent)}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedAgent && selectedAgent.id === agent.id ? '#ffff00' : '',
                        border: selectedAgent && selectedAgent.id === agent.id ? '2px solid #ff0000' : ''
                      }}
                    >
                      <Table.Td><strong>{agent.agent_number}</strong></Table.Td>
                      <Table.Td>{agent.name || 'N/A'}</Table.Td>
                      <Table.Td>{agent.agent_type_name || 'N/A'}</Table.Td>
                      <Table.Td>{agent.commission_rate || 'N/A'}</Table.Td>
                      <Table.Td>{agent.city || 'N/A'}</Table.Td>
                      <Table.Td>{agent.state || 'N/A'}</Table.Td>
                      <Table.Td>
                        <Badge color={agent.active ? 'green' : 'red'}>
                          {agent.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon 
                            color="orange" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(agent);
                            }}
                          >
                            <IconEdit size="1rem" />
                          </ActionIcon>
                          <ActionIcon 
                            color="red" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(agent);
                            }}
                          >
                            <IconTrash size="1rem" />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              {totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={totalPages}
                    size="sm"
                  />
                </Group>
              )}
            </Paper>

            {/* Agent Details Section */}
            {selectedAgent && (
              <Paper shadow="sm" p="md" radius="md" withBorder>
                <Tabs value={activeTab} onChange={setActiveTab}>
                  <Tabs.List>
                    <Tabs.Tab value="details">Details</Tabs.Tab>
                    <Tabs.Tab value="performance">Performance</Tabs.Tab>
                    <Tabs.Tab value="transactions">Transactions</Tabs.Tab>
                    <Tabs.Tab value="reservations">Reservations</Tabs.Tab>
                    <Tabs.Tab value="notes">Notes</Tabs.Tab>
                  </Tabs.List>
                  
                  <Tabs.Panel value="details" pt="md">
                    <Grid>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Agent Number:</strong> {selectedAgent.agent_number}</Text>
                          <Text><strong>Name:</strong> {selectedAgent.name || 'N/A'}</Text>
                          <Text><strong>Agent Type:</strong> {selectedAgent.agent_type_name || 'N/A'}</Text>
                          <Text><strong>Commission Rate:</strong> {selectedAgent.commission_rate || 'N/A'}</Text>
                          <Text><strong>Phone 1:</strong> {selectedAgent.phone1 || 'N/A'}</Text>
                          <Text><strong>Phone 2:</strong> {selectedAgent.phone2 || 'N/A'}</Text>
                          <Text><strong>Status:</strong> 
                            <Badge color={selectedAgent.active ? 'green' : 'red'} ml="xs">
                              {selectedAgent.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </Text>
                        </Stack>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Address 1:</strong> {selectedAgent.address1 || 'N/A'}</Text>
                          <Text><strong>Address 2:</strong> {selectedAgent.address2 || 'N/A'}</Text>
                          <Text><strong>City:</strong> {selectedAgent.city || 'N/A'}</Text>
                          <Text><strong>State:</strong> {selectedAgent.state || 'N/A'}</Text>
                          <Text><strong>Zipcode:</strong> {selectedAgent.zipcode || 'N/A'}</Text>
                          <Text><strong>County:</strong> {selectedAgent.county || 'N/A'}</Text>
                        </Stack>
                      </Grid.Col>
                    </Grid>
                  </Tabs.Panel>
                  
                  <Tabs.Panel value="performance" pt="md">
                    <Text>Performance Information</Text>
                    <Text size="sm" c="dimmed">This tab will contain performance metrics, sales data, and commission tracking for this agent.</Text>
                  </Tabs.Panel>
                  
                  <Tabs.Panel value="transactions" pt="md">
                    <Text>Transaction History</Text>
                    <Text size="sm" c="dimmed">This tab will contain transaction records, commission history, and financial information.</Text>
                  </Tabs.Panel>
                  
                  <Tabs.Panel value="reservations" pt="md">
                    <Text>Reservations</Text>
                    <Text size="sm" c="dimmed">This tab will contain reservation history, upcoming stays, and booking information for this agent.</Text>
                  </Tabs.Panel>
                  
                  <Tabs.Panel value="notes" pt="md">
                    <Text>Notes</Text>
                    <Text size="sm" c="dimmed">This tab will contain internal notes, communication history, and important information about this agent.</Text>
                  </Tabs.Panel>
                </Tabs>
              </Paper>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Edit Agent Modal */}
      <Modal opened={showEditModal} onClose={handleCancelEdit} title="Edit Agent" size="lg">
        {editingAgent && (
          <Stack gap="md">
            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Agent Number"
                  value={editingAgent.agent_number}
                  disabled
                  description="Agent number cannot be changed"
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Agent Type"
                  name="agent_type_id"
                  value={editingAgent.agent_type_id || ''}
                  onChange={(value) => setEditingAgent(prev => ({ ...prev, agent_type_id: value }))}
                  data={agentTypes.map(type => ({ value: type.id.toString(), label: type.name }))}
                  placeholder="Select Agent Type"
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Name"
                  name="name"
                  value={editingAgent.name || ''}
                  onChange={handleInputChange}
                  required
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Commission"
                  name="agent_commission_id"
                  value={editingAgent.agent_commission_id || ''}
                  onChange={(value) => setEditingAgent(prev => ({ ...prev, agent_commission_id: value }))}
                  data={agentCommissions.map(commission => ({ value: commission.id.toString(), label: commission.commission_rate }))}
                  placeholder="Select Commission"
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Address 1"
                  name="address1"
                  value={editingAgent.address1 || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Address 2"
                  name="address2"
                  value={editingAgent.address2 || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={4}>
                <TextInput
                  label="City"
                  name="city"
                  value={editingAgent.city || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  label="State"
                  name="state"
                  value={editingAgent.state || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  label="Zipcode"
                  name="zipcode"
                  value={editingAgent.zipcode || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="County"
                  name="county"
                  value={editingAgent.county || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Status"
                  name="active"
                  type="checkbox"
                  checked={editingAgent.active}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Phone 1"
                  name="phone1"
                  value={editingAgent.phone1 || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Phone 2"
                  name="phone2"
                  value={editingAgent.phone2 || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal opened={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Delete">
        <Stack gap="md">
          <Text>
            Are you sure you want to delete agent <strong>{agentToDelete?.agent_number}</strong> ({agentToDelete?.name})?
            This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirmDelete}
              loading={deleteLoading}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default AgentsListScreen;


